<template>
  <hit-modal :title="displayTitle" full-height @cancel="onCancel">
    <iframe class="w-full h-full" :src="srcWithParams"></iframe>
  </hit-modal>
</template>

<script>
import {ref, watch, onMounted, onBeforeUnmount} from 'vue';
import {HitModal} from '../modal';

export default {
  name: 'HitPopupIframe',
  components: {
    HitModal,
  },

  props: {
    /**
     * String displayed as panel title
     */
    title: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Url to the html file we want to load in the iframe
     */
    src: {
      type: String,
      required: true,
    },
  },

  emits: [
    'cancel',
    'continue',
    'navigate',
    'refresh-parent',
    'notify',
    'set-title',
    'open-url',
    'download-file',
  ],

  setup(props, {emit}) {
    const displayTitle = ref(props.title);
    watch(
      () => props.title,
      (v) => {
        displayTitle.value = v;
      },
      {immediate: true}
    );

    /**
     * Listens for messages from iframes (custom buttons).
     * Supported messages (send via window.parent.postMessage(..., '*')):
     *
     * - Close (optional payload): { type: 'hit-popup-iframe-close', payload?: any }
     * - Navigate: { type: 'hit-popup-iframe-navigate', path: string } or { type: 'hit-popup-iframe-navigate', name: string, params?: object }
     * - Refresh parent: { type: 'hit-popup-iframe-refresh-parent' }
     * - Notify (banner): { type: 'hit-popup-iframe-notify', level: 'success'|'error'|'warning'|'info', message: string }
     * - Set modal title: { type: 'hit-popup-iframe-set-title', title: string }
     * - Open URL: { type: 'hit-popup-iframe-open-url', url: string, target?: '_blank'|'_self' }
     * - Download file: { type: 'hit-popup-iframe-download-file', filename: string, mimeType?: string, base64?: string, url?: string }
     * - Continue (action override): { type: 'hit-popup-iframe-continue', payload?: any }
     */
    function handleMessage(event) {
      const data = event.data;
      if (!data || typeof data !== 'object' || !data.type) return;

      switch (data.type) {
        case 'hit-popup-iframe-close':
          emit('cancel', data.payload);
          break;
        case 'hit-popup-iframe-continue':
          emit('continue', data.payload);
          break;
        case 'hit-popup-iframe-navigate':
          emit('navigate', {
            path: data.path,
            name: data.name,
            params: data.params,
          });
          break;
        case 'hit-popup-iframe-refresh-parent':
          emit('refresh-parent');
          break;
        case 'hit-popup-iframe-notify':
          emit('notify', {
            level: data.level || 'info',
            message: data.message || '',
          });
          break;
        case 'hit-popup-iframe-set-title':
          if (typeof data.title === 'string') displayTitle.value = data.title;
          emit('set-title', data.title);
          break;
        case 'hit-popup-iframe-open-url':
          if (data.url) {
            emit('open-url', {
              url: data.url,
              target: data.target || '_blank',
            });
          }
          break;
        case 'hit-popup-iframe-download-file':
          emit('download-file', {
            filename: data.filename,
            mimeType: data.mimeType,
            base64: data.base64,
            url: data.url,
          });
          break;
        default:
          break;
      }
    }

    onMounted(() => {
      window.addEventListener('message', handleMessage);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('message', handleMessage);
    });

    function onCancel() {
      emit('cancel');
    }

    return {displayTitle, onCancel};
  },

  computed: {
    srcWithParams() {
      return `${this.src}&route=${this.$route.fullPath}&customButton=true`;
    },
  },
};
</script>
